import { createTheme, Theme, ThemeOptions } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";

export const designColors = {
  gray: {
    "000": "#FFFFFF",
    100: "#F2F2F2",
    200: "#D9DCDE",
    300: "#B3B5B8",
    400: "#8E9193",
    500: "#74777A",
    600: "#65686B",
    700: "#44474B",
    800: "#2D3135",
    850: "#232629",
    900: "#161A1E",
    950: "#141618",
  },
  gold: {
    100: "#FFFAF5",
    200: "#E7C5A1",
    300: "#D9B179",
    400: "#CC9E5A",
    500: "#A27D49",
    600: "#85663C",
    700: "#6A5130",
    800: "#503D24",
    900: "#372A19",
  },
  green: {
    500: "#139E79",
  },
  red: {
    500: "#E05357",
  },
};

const themeCommonConfig: ThemeOptions = {
  typography: {
    fontFamily: 'var(--font-montserrat), "PingFang SC", "PingFang TC", "Microsoft YaHei", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "2rem",
          padding: "0.5rem 1.5rem",
          fontSize: "1rem",
          lineHeight: "1.5rem",
          textTransform: "none",
          boxShadow: "none", // 去掉默认投影
          "&:hover": {
            boxShadow: "none", // 确保悬停状态也没有投影
          },
          "&:active": {
            boxShadow: "none", // 确保激活状态也没有投影
          },
          "&:focus": {
            boxShadow: "none", // 确保聚焦状态也没有投影
          },
        },
        sizeLarge: {
          padding: "0.75rem 1.5rem",
        },
        containedPrimary: {
          backgroundColor: designColors.gold[400],
          color: designColors.gray["000"],
          "&.Mui-disabled": {
            backgroundColor: designColors.gold[400],
            color: designColors.gray["000"],
            opacity: 0.3,
          },
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        outlined: {
          boxShadow: "none",
        },
        text: {
          boxShadow: "none",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "0.5rem",
            "& fieldset": {
              border: "none", // 去掉边框
            },
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0, // 0-640px
      sm: 641, // 641-900px
      md: 901, // 901-1200px
      lg: 1201, // 1201px以上
      xl: 1536, // 不使用 1536
    },
  },
};

// 需要在 Material-UI 的类型定义中添加 tertiary 颜色
declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}

// 创建亮色主题
export const lightTheme = createTheme(
  deepmerge(themeCommonConfig, {
    palette: {
      mode: "light",
      primary: {
        main: designColors.gold[400],
        light: designColors.gold[300],
        dark: designColors.gold[500],
        contrastText: designColors.gray[900],
      },
      secondary: {
        main: designColors.gray[200],
        light: designColors.gray[100],
        dark: designColors.gray[300],
        contrastText: designColors.gray[800],
      },
      tertiary: {
        main: designColors.gray[700],
        light: designColors.gray[600],
        dark: designColors.gray[800],
        contrastText: designColors.gray[100],
      },
      background: {
        default: designColors.gold[100],
        paper: designColors.gray[100],
        paperMedium: designColors.gray[100],
        paperExtreme: designColors.gray["000"],
        footer: designColors.gray[900],
        footerTagline: designColors.gray[800],
        homeInverse: designColors.gray[900],
        modal: "rgba(255, 255, 255, 0.80)",
        popover: "rgba(255, 255, 255, 0.85)",
        hover: "rgba(255, 255, 255, 0.56)",
      },
      text: {
        primary: designColors.gray[800],
        primary2: designColors.gray[700],
        secondary: designColors.gray[600],
        tertiary: designColors.gray[400],
        footer: designColors.gray["000"],
        footerLight1: designColors.gray[500],
        footerLight2: designColors.gray[800],
        footerLight3: designColors.gray[500],
        homeInv1: designColors.gray[100],
        homeInv2: designColors.gray[300],
      },
      gray: designColors.gray,
      gold: designColors.gold,
      DateRangeQuickPicker: {
        bg: designColors.gray[200],
        button: {
          bg: designColors.gray["000"],
        },
      },
      Dialog: {
        bg: "rgba(255, 255, 255, 0.90)",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          containedSecondary: {
            backgroundColor: designColors.gray[200],
            color: designColors.gray[800],
          },
          outlinedSecondary: {
            color: designColors.gray[600],
            border: `1px solid ${designColors.gray[600]}`,
          },
        },
        variants: [
          {
            props: { variant: "text", color: "tertiary" },
            style: ({ theme }: any) => ({
              color: theme.palette.tertiary.main,
              "&:hover": {
                backgroundColor: theme.palette.tertiary.light + "20",
              },
            }),
          },
          {
            props: { variant: "outlined", color: "tertiary" },
            style: ({ theme }: any) => ({
              color: theme.palette.tertiary.main,
              borderColor: theme.palette.tertiary.main,
              "&:hover": {
                backgroundColor: theme.palette.tertiary.light + "20",
                borderColor: theme.palette.tertiary.dark,
              },
            }),
          },
          {
            props: { variant: "contained", color: "tertiary" },
            style: ({ theme }: any) => ({
              backgroundColor: designColors.gray[800],
              color: designColors.gray[100],
              "&:hover": {
                backgroundColor: designColors.gray[700],
              },
            }),
          },
        ],
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: designColors.gray["000"],
            },
          },
        },
      },
    },
  } as ThemeOptions)
);

// 创建暗色主题
export const darkTheme = createTheme(
  deepmerge(themeCommonConfig, {
    palette: {
      mode: "dark",
      primary: {
        ...designColors.gold,
        main: designColors.gold[400],
        light: designColors.gold[300],
        dark: designColors.gold[500],
        contrastText: designColors.gray[900],
      },
      secondary: {
        main: designColors.gray[700],
        light: designColors.gray[600],
        dark: designColors.gray[800],
        contrastText: designColors.gray[100],
      },
      tertiary: {
        main: designColors.gray[200],
        light: designColors.gray[300],
        dark: designColors.gray[100],
        contrastText: designColors.gray[800],
      },
      background: {
        default: designColors.gray[900],
        paper: designColors.gray[800],
        paperMedium: designColors.gray[850],
        paperExtreme: designColors.gray[900],
        footer: designColors.gold[400],
        footerTagline: designColors.gold[500],
        homeInverse: designColors.gold[100],
        modal: "rgba(35, 38, 41, 0.80)",
        popover: "rgba(35, 38, 41, 0.90)",
        hover: "rgba(255, 255, 255, 0.08)",
      },
      text: {
        primary: designColors.gray["000"],
        primary2: designColors.gray[100],
        secondary: designColors.gray[300],
        tertiary: designColors.gray[500],
        footer: designColors.gray["000"],
        footerLight1: designColors.gold[700],
        footerLight2: designColors.gold[600],
        footerLight3: designColors.gold[500],
        homeInv1: designColors.gray[800],
        homeInv2: designColors.gray[600],
      },
      gray: designColors.gray,
      gold: designColors.gold,
      DateRangeQuickPicker: {
        bg: designColors.gray[800],
        button: {
          bg: designColors.gray[700],
        },
      },
      Dialog: {
        bg: "rgba(45, 49, 53, 0.90)",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          containedSecondary: {
            backgroundColor: designColors.gray[700],
            color: designColors.gray[100],
          },
          outlinedSecondary: {
            color: designColors.gray[300],
            border: `1px solid ${designColors.gray[500]}`,
          },
        },
        variants: [
          {
            props: { variant: "text", color: "tertiary" },
            style: ({ theme }: any) => ({
              color: theme.palette.tertiary.main,
              "&:hover": {
                backgroundColor: theme.palette.tertiary.light + "20",
              },
            }),
          },
          {
            props: { variant: "outlined", color: "tertiary" },
            style: ({ theme }: any) => ({
              color: theme.palette.tertiary.main,
              borderColor: theme.palette.tertiary.main,
              "&:hover": {
                backgroundColor: theme.palette.tertiary.light + "20",
                borderColor: theme.palette.tertiary.dark,
              },
            }),
          },
          {
            props: { variant: "contained", color: "tertiary" },
            style: ({ theme }: any) => ({
              backgroundColor: designColors.gray[100],
              color: designColors.gray[900],
              "&:hover": {
                backgroundColor: designColors.gray["000"],
              },
            }),
          },
        ],
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: designColors.gray[700],
            },
          },
        },
      },
    },
  } as ThemeOptions)
);

// 导出类型
export type AppTheme = Theme;

export const CANDLE_COLOR = {
  BULLISH: designColors.green[500],
  BEARISH: designColors.red[500],
};

function commonAxis(theme: "light" | "dark", splitLineShow = true) {
  if (theme === "dark") {
    return {
      axisLine: {
        lineStyle: { color: designColors.gray[700] },
      },
      axisLabel: { color: designColors.gray[600] },
      splitLine: {
        show: splitLineShow,
        lineStyle: {
          color: designColors.gray[700],
        },
      },
    };
  } else {
    return {
      axisLine: {
        lineStyle: { color: designColors.gray[600] },
      },
      axisLabel: { color: designColors.gray[600] },
      splitLine: {
        show: splitLineShow,
        lineStyle: {
          color: designColors.gray[200],
        },
      },
    };
  }
}

export const CHART_COLORS = [
  "#E8B325", // 金色
  "#A477C9", // 紫色
  "#1D63F0", // 蓝色
  "#F08737", // 橙色
  "#8665E5", // 紫色
  "#01C6FA", // 浅蓝色
  "#F2633C", // 橙色
  "#69628A", // 深紫色
];

const chartCommonConfig = {
  color: CHART_COLORS,
  title: {
    show: false,
    height: 0,
  },
  grid: {
    top: "4%",
  },
  legend: {
    icon: "circle",
  },
};

export const chartDarkTheme = deepmerge(chartCommonConfig, {
  textStyle: {
    color: "#FFFFFF",
  },
  legend: {
    textStyle: {
      color: "#FFFFFF",
    },
  },
  title: {
    textStyle: {
      color: "#FFFFFF",
    },
  },
  valueAxis: commonAxis("dark"),
  timeAxis: commonAxis("dark", false),
  categoryAxis: commonAxis("dark", false),
  logAxis: commonAxis("dark"),
});

export const chartLightTheme = deepmerge(chartCommonConfig, {
  textStyle: {
    color: designColors.gray[800],
  },
  axisLine: {
    lineStyle: {
      color: designColors.gray[600],
    },
  },
  axisLabel: {
    color: designColors.gray[600],
  },
  legend: {
    textStyle: {
      color: designColors.gray[600],
    },
  },
  title: {
    textStyle: {
      color: designColors.gray[800],
    },
  },
  valueAxis: commonAxis("light"),
  timeAxis: commonAxis("light", false),
  categoryAxis: commonAxis("light", false),
  logAxis: commonAxis("light"),
});
